import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Snackbar,
  Portal,
  Stack,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  PhoneInTalk as PhoneInTalkIcon,
  LocalTaxi as TaxiIcon,
  SmartToy as SmartToyIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../redux/slices/authSlice';
import { useRideDialog } from '../rides/RideDialogProvider';
import {
  ACTIVE_CALL_MAX_AGE_MS,
  canReceiveTelephonyCalls,
  subscribeToIncomingCalls,
  toDate
} from '../../services/telephonyService';
import tabCoordinator from '../../services/tabCoordinator';
import useFullscreenElement from '../../hooks/useFullscreenElement';

const MAX_DISMISSED_CALLS = 100;

const dismissedCallsStorageKey = (userId) =>
  `nextcab.telephony.dismissedCalls.${userId || 'anonymous'}`;

const loadDismissedCallIds = (userId) => {
  if (!userId) return new Set();
  try {
    const storedIds = JSON.parse(localStorage.getItem(dismissedCallsStorageKey(userId)) || '[]');
    return new Set(Array.isArray(storedIds) ? storedIds.filter((id) => typeof id === 'string') : []);
  } catch (_) {
    return new Set();
  }
};

const saveDismissedCallIds = (userId, dismissedIds) => {
  if (!userId) return;
  try {
    const ids = Array.from(dismissedIds).slice(-MAX_DISMISSED_CALLS);
    localStorage.setItem(dismissedCallsStorageKey(userId), JSON.stringify(ids));
  } catch (_) {
    // Der Hinweis bleibt innerhalb der aktuellen Sitzung trotzdem ausgeblendet.
  }
};

// Tastenhinweis im Button („Alt+N"). Randfarbe = Schriftfarbe, damit er im
// gefüllten und im Text-Button gleich lesbar ist.
const KeyHint = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      ml: 1,
      px: 0.5,
      fontFamily: 'inherit',
      fontSize: '0.7rem',
      fontWeight: 600,
      lineHeight: 1.5,
      border: '1px solid currentColor',
      borderRadius: 0.5,
      opacity: 0.8,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
);

const IncomingCallNotifier = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openRideDialog } = useRideDialog() || {};
  const fullscreenElement = useFullscreenElement();
  const user = useSelector(selectCurrentUser);
  const userId = user?.uid || '';
  const [calls, setCalls] = useState([]);
  const [error, setError] = useState('');
  const [dismissedIds, setDismissedIds] = useState(() => loadDismissedCallIds(userId));

  useEffect(() => {
    setDismissedIds(loadDismissedCallIds(userId));
  }, [userId]);

  useEffect(() => {
    if (!canReceiveTelephonyCalls(user)) return () => {};
    return subscribeToIncomingCalls(user, setCalls, (snapshotError) => {
      setError(snapshotError.message || 'Anrufe konnten nicht geladen werden.');
    });
  }, [user]);

  // Wird der Anruf in einem anderen Portal-Fenster ausgeblendet, verschwindet
  // er auch hier. Der localStorage-Speicher unten wird zwar von allen Fenstern
  // geteilt, aber nur beim Mount gelesen — ohne diese Meldung bliebe der
  // Hinweis bis zum naechsten Reload stehen.
  useEffect(() => tabCoordinator.onDismiss('call', (callId) => {
    setDismissedIds((current) => {
      if (current.has(callId)) return current;
      const next = new Set(current);
      next.add(callId);
      saveDismissedCallIds(userId, next);
      return next;
    });
  }), [userId]);

  const activeCall = useMemo(() => calls.find((call) => {
    if (dismissedIds.has(call.id)) return false;
    if (!['ringing', 'connected', 'missed'].includes(call.state)) return false;
    const startedAt = toDate(call.startedAt);
    return startedAt && Date.now() - startedAt.getTime() <= ACTIVE_CALL_MAX_AGE_MS;
  }) || null, [calls, dismissedIds]);

  const dismiss = () => {
    if (!activeCall) return;
    const callId = activeCall.id;
    setDismissedIds((current) => {
      const next = new Set(current);
      next.add(callId);
      saveDismissedCallIds(userId, next);
      return next;
    });
    tabCoordinator.broadcastDismiss('call', callId);
  };

  const prepareRide = () => {
    if (!activeCall) return;
    const customer = activeCall.matchedCustomer || null;
    const isDashboard = location.pathname === '/dashboard';

    if (isDashboard) {
      // Auf dem Dashboard: das kompakte Schnellformular in der LiveMap-Fahrtenliste
      // öffnen (vorbefüllt mit den Anruferdaten) – kein großer Dialog.
      navigate('/dashboard', {
        state: {
          openQuickRide: {
            incomingCallSessionId: activeCall.id,
            customerId: customer?.id || '',
            customerName: customer?.name || '',
            customerPhone: activeCall.callerNumberE164 || activeCall.callerNumberRaw || '',
            pickupAddress: customer?.address || ''
          }
        }
      });
    } else {
      // Woanders: den großen Fahrt-Dialog an Ort und Stelle öffnen, ohne wegzunavigieren.
      openRideDialog?.({
        mode: 'create',
        draft: {
          incomingCallDraft: {
            incomingCallSessionId: activeCall.id,
            customerId: customer?.id || '',
            customerName: customer?.name || '',
            customerPhone: activeCall.callerNumberE164 || activeCall.callerNumberRaw || '',
            pickupAddress: customer?.address || ''
          }
        }
      });
    }
    dismiss();
  };

  // Tastatur: Alt+N = Fahrt vorbereiten, Alt+X = ausblenden – aus jedem Feld,
  // auch mitten in einer anderen Erfassung. Bewusst nicht Enter: Das springt in
  // den Formularen ins nächste Feld und würde dort den Anruf übernehmen.
  // Aufgelöst über die physische Taste (event.code): Auf macOS liefert ⌥+N
  // sonst ein Sonderzeichen. Wirkt nur im Fenster mit Fokus; das Ausblenden
  // verteilt der tabCoordinator an alle Fenster.
  // Siehe docs/TASTATURGEFUEHRTE_FAHRTERFASSUNG.md §3.7.
  const callKeyActionsRef = useRef({});
  callKeyActionsRef.current = { prepareRide, dismiss };
  const activeCallId = activeCall?.id || null;
  const activeCallIsAi = activeCall?.source === 'ai_telephonist';
  useEffect(() => {
    if (!activeCallId) return undefined;
    const onKeyDown = (event) => {
      if (!event.altKey || event.ctrlKey || event.metaKey || event.repeat) return;
      if (event.code === 'KeyN' && !activeCallIsAi) {
        event.preventDefault();
        event.stopPropagation();
        callKeyActionsRef.current.prepareRide();
      } else if (event.code === 'KeyX') {
        event.preventDefault();
        event.stopPropagation();
        callKeyActionsRef.current.dismiss();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [activeCallId, activeCallIsAi]);

  if (!canReceiveTelephonyCalls(user)) return null;

  // Im Vollbild der LiveMap in das Vollbild-Element rendern – sonst sieht den
  // Anruf niemand, wenn nur dieses Fenster offen ist. Bei zwei Bildschirmen
  // erscheint er dann auf beiden; Ton und Ausblenden koordiniert der
  // tabCoordinator ohnehin fensterübergreifend.
  return (
    <Portal container={fullscreenElement}>
      <Snackbar
        open={Boolean(activeCall)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 8, maxWidth: 440 }}
      >
        <Card
          elevation={8}
          sx={{
            width: '100%',
            borderLeft: 5,
            borderColor: activeCall?.source === 'ai_telephonist' ? 'info.main' : 'primary.main'
          }}
        >
          {activeCall && (() => {
            // KI-Telefonist-Anrufe: nur informativ anzeigen — die KI bearbeitet den
            // Anruf bereits, der Disponent muss (und soll) nichts vorbereiten.
            const isAiCall = activeCall.source === 'ai_telephonist';
            return (
            <>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  {isAiCall ? (
                    <SmartToyIcon color="info" sx={{ mt: 0.5 }} />
                  ) : (
                    <PhoneInTalkIcon color="primary" sx={{ mt: 0.5 }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="overline"
                      color={isAiCall ? 'info.main' : 'primary'}
                      fontWeight="bold"
                    >
                      {isAiCall
                        ? '🤖 KI-Telefonist im Gespräch'
                        : activeCall.state === 'missed' ? 'Verpasster Anruf' : 'Eingehender Anruf'}
                    </Typography>
                    <Typography variant="h6">
                      {activeCall.matchedCustomer?.name ||
                        (activeCall.numberPresentation === 'withheld' ? 'Nummer unterdrückt' : 'Unbekannter Anrufer')}
                    </Typography>
                    <Typography variant="body1">
                      {activeCall.callerNumberE164 || activeCall.callerNumberRaw || 'Keine Rufnummer'}
                    </Typography>
                    {activeCall.matchedCustomer?.address && (
                      <Typography variant="body2" color="text.secondary">
                        {activeCall.matchedCustomer.address}
                      </Typography>
                    )}
                    {isAiCall && (
                      <Typography variant="body2" color="text.secondary">
                        Der KI-Telefonist bearbeitet diesen Anruf — kein Eingreifen nötig.
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                {!isAiCall && (
                  <Button variant="contained" startIcon={<TaxiIcon />} onClick={prepareRide}>
                    Fahrt vorbereiten
                    <KeyHint>Alt+N</KeyHint>
                  </Button>
                )}
                {!isAiCall && activeCall.matchedCustomer?.id && (
                  <Button
                    startIcon={<PersonIcon />}
                    onClick={() => navigate(`/kunden/${activeCall.matchedCustomer.id}`)}
                  >
                    Kunde
                  </Button>
                )}
                <Button color="inherit" startIcon={<CloseIcon />} onClick={dismiss}>
                  Ausblenden
                  <KeyHint>Alt+X</KeyHint>
                </Button>
              </CardActions>
            </>
            );
          })()}
        </Card>
      </Snackbar>
      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    </Portal>
  );
};

export default IncomingCallNotifier;
